import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useVenue } from "../lib/venue";
import { joinMailingList } from "../lib/api";
import { pushCtaClick, pushFormInteraction } from "../lib/analytics";
import { collectComingSoonSocials } from "../lib/coming-soon-socials";
import { serifStackFor, serifGoogleFamilyFor } from "../lib/coming-soon-fonts";

// Same-origin path -> client-side navigate (smooth). Absolute or external URL
// (protocol / scheme) -> plain anchor (browser default). Kept separate from the
// button so the CSS animation state stays with the button, not the check.
function isSameOriginPath(href: string): boolean {
  if (!href) return false;
  if (href.startsWith("/") && !href.startsWith("//")) return true;
  return false;
}

// A slick "coming soon" landing with a mailing-list capture, for a venue that's
// pre-launch (e.g. Toka Saunas before sauna bookings open). Config lives on
// appConfig.comingSoon so it's editable per venue. The hero fills the screen;
// optional `ritual` / `land` sections turn it into a scrolling marketing page.
export type ComingSoonConfig = {
  enabled?: boolean;
  eyebrow?: string; // small uppercase label
  headline?: string; // the big line
  subline?: string; // supporting sentence
  ctaLabel?: string; // button label
  // When set, the hero CTA becomes a link to this href (e.g. "/book") instead
  // of the mailing-list capture form, the "launch flip" from pre-sale splash
  // to a live booking landing.
  ctaHref?: string;
  backdrop?: string; // full-bleed background image (landscape / wide screens)
  backdropPortrait?: string; // portrait/mobile background (shown on portrait screens)
  logo?: string; // wordmark/logo over the name
  hideName?: boolean; // suppress the venue-name heading (e.g. the photo already carries the branding)
  // Hero text colour (headline + name). Defaults to white for legibility on a
  // dark photo; a venue can set its own (e.g. a warm cream) from the editor.
  heroTextColor?: string;
  // The confirmation shown after a successful signup (hero + waitlist). Editable
  // so a venue can drop the default emoji / set their own voice.
  successMessage?: string;
  // Show the venue's social links (from footer.socials) on the hero. On by
  // default when any exist, the coming-soon page has no footer of its own.
  hideSocials?: boolean;
  // Also collect a name / phone number on the hero signup (email-only by
  // default). `phoneRequired` makes phone mandatory wherever it's collected
  // (hero + the foot-of-page waitlist). All editable from the Website admin.
  collectName?: boolean;
  collectPhone?: boolean;
  phoneRequired?: boolean;
  // Optional scroll sections below the hero, present ⇒ the page scrolls.
  ritual?: {
    eyebrow?: string;
    headline?: string;
    markUrl?: string; // optional icon (alpha mask), recoloured per step
    steps?: { title: string; body: string; color?: string }[];
  };
  land?: {
    eyebrow?: string;
    headline?: string;
    body?: string[];
    photo?: string;
    ctaLabel?: string;
    ctaHref?: string;
  };
  // Foot-of-page "launch waitlist", a designed two-column capture (copy left,
  // Name/Email/Phone + optional "which window?" radios right). Richer than the
  // hero's single-field email grab. Shown at the bottom in mailing-list mode.
  waitlist?: {
    eyebrow?: string;
    headline?: string;
    body?: string;
    windows?: { label: string; value: string }[];
    // Section colours (default: venue accent bg + dark ink text). Editable.
    bgColor?: string;
    textColor?: string;
  };
  // Optional display serif for the editorial scroll sections. Falls back to the
  // default stack below. White-label: a venue sets its own without a code change.
  // Values map to entries in web/src/lib/coming-soon-fonts.ts (COMING_SOON_SERIFS).
  serifFont?: string;
  // Use the same serif for the H1 venue name too, defaults false (H1 uses the
  // venue's own base font). Tyler @ Toka flagged 2026-08-25 that the name font
  // vs the section-headline font looked inconsistent — this lets an admin unify
  // them without changing the venue's global branding font.
  nameUsesSerif?: boolean;
};

// WHITE-LABEL: the scroll sections theme off the VENUE'S OWN accent
// (var(--accent), set by the shell from Venue.accentColor), falling back to a
// neutral editorial copper only when a venue has no accent, so a second venue
// never inherits Toka's brand. The serif is a generic high-contrast default,
// overridable per venue via appConfig.comingSoon.serifFont (piped in as the
// --coming-soon-serif CSS var on the root below).
const DEFAULT_SERIF = '"Didot", "Bodoni 72", "Playfair Display", Georgia, "Times New Roman", serif';

// Inject the Google Font <link> for the chosen serif when it's a known family
// (see COMING_SOON_SERIFS). No-op for "system" / unknown values (they fall
// back through the stack). Idempotent: adds the link once per family, keyed
// on an id, safe to call on every render.
function useComingSoonSerifLoader(serifFont: string | null | undefined) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const g = serifGoogleFamilyFor(serifFont);
    if (!g) return;
    const id = `gf-cs-${g.family.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${g.family.replace(/\s+/g, "+")}:wght@${g.weights.join(";")}&display=swap`;
    document.head.appendChild(link);
  }, [serifFont]);
}
// Shared signup CTA label, drives BOTH the hero form button and the foot-of-page
// waitlist button so the two always read the same (edited once via
// appConfig.comingSoon.ctaLabel in the Website admin). Falls back to this default.
const CTA_DEFAULT = "Join the list";
const SERIF = "var(--coming-soon-serif)";
const EDITORIAL_ACCENT = "var(--accent, #b5623f)";

// Social icon paths, shared with the site footer (Instagram, Facebook).
const SOCIAL_PATHS: Record<string, string | undefined> = {
  instagram:
    "M12 2.2c3.2 0 3.6 0 4.8.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23a3.7 3.7 0 0 1-.9 1.38 3.7 3.7 0 0 1-1.38.9c-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.6 2.2 15.2 2.2 12s0-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2m0 5.4a4.4 4.4 0 1 0 0 8.8 4.4 4.4 0 0 0 0-8.8m0 7.26a2.86 2.86 0 1 1 0-5.72 2.86 2.86 0 0 1 0 5.72m5.6-7.44a1.03 1.03 0 1 1-2.06 0 1.03 1.03 0 0 1 2.06 0",
  facebook:
    "M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12",
};

export function ComingSoonSplash() {
  const { data } = useVenue();
  const venue = data?.venue;
  const cfg = (venue?.appConfig?.comingSoon ?? {}) as ComingSoonConfig;
  useComingSoonSerifLoader(cfg.serifFont);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");
  const [error, setError] = useState("");
  // Fade-out state for the launch-flip CTA -> internal route transition
  // (splash -> /book). Toggled on click, then a matching 220ms CSS animation
  // runs before we navigate. External / absolute ctaHrefs skip this entirely
  // (the browser handles them as normal anchors).
  const [leaving, setLeaving] = useState(false);

  // Preload the /book route bundle as soon as the splash mounts, so the CTA
  // click doesn't fire during a Suspense code-split load and flash the white
  // fallback. Fires only when the launch-flip target is /book; any other path
  // is left alone. Fires once per mount; no-op on the second mount because
  // the module is cached by then.
  useEffect(() => {
    if (cfg.ctaHref === "/book") {
      // Silently preload; failures aren't user-visible and would surface again
      // via the router's own error handling on actual navigation.
      void import("./book-entry");
    }
  }, [cfg.ctaHref]);
  const collectPhone = !!cfg.collectPhone;
  const collectName = !!cfg.collectName;
  const phoneRequired = !!cfg.phoneRequired;
  // A required phone must have at least a few digits; optional/hidden always passes.
  const phoneOk = !collectPhone || !phoneRequired || phone.replace(/\D/g, "").length >= 6;

  const name = venue?.appName || venue?.name || "";
  const eyebrow = cfg.eyebrow ?? "Coming soon";
  const headline = cfg.headline ?? "Something new is on the way.";
  const subline = cfg.subline ?? "Join the list to be first through the door.";
  const cta = cfg.ctaLabel || CTA_DEFAULT;
  const successMessage = cfg.successMessage ?? "You're on the list. We'll be in touch the moment doors open.";
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const hasPhoto = !!(cfg.backdrop || cfg.backdropPortrait);
  const hasSections = !!(cfg.ritual || cfg.land);
  // Social links from the venue's footer config. Surfaced BOTH in the hero
  // (compact) and in the new bottom ComingSoonFooter (larger, natural
  // footer position), so a small-phone user with the hero pushed by the
  // form still reaches them by scrolling to the bottom.
  const socialLinkEntries = collectComingSoonSocials({
    socials: venue?.appConfig?.footer?.socials,
    hideSocials: cfg.hideSocials,
  });
  const socialLinks = socialLinkEntries.map((s) => [s.key, s.href] as [string, string]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!venue?.id || !emailValid || !phoneOk || status === "loading") return;
    pushCtaClick("subscribe");
    setStatus("loading");
    setError("");
    try {
      await joinMailingList(
        venue.id,
        email.trim(),
        "coming_soon",
        collectPhone && phone.trim() ? phone.trim() : undefined,
        undefined,
        collectName && fullName.trim() ? fullName.trim() : undefined,
      );
      pushFormInteraction("success");
      setStatus("done");
    } catch (err) {
      pushFormInteraction("fail");
      setError(err instanceof Error ? err.message : "Something went wrong, try again.");
      setStatus("idle");
    }
  }

  return (
    <div
      // Normal document flow (NOT a fixed overlay) so mobile Safari scrolls
      // natively both ways, a `fixed inset-0` + `overscroll-contain` scroller
      // traps the scroll on iOS (can go down, not back up: Tyler's report).
      // The hero is min-h-[100dvh] so it still fills the first screen.
      className={`relative min-h-[100dvh] w-full bg-black text-white ${hasSections ? "" : "overflow-hidden"} ${leaving ? "page-fade-out" : "page-fade-in"}`}
      style={{ ["--coming-soon-serif" as string]: cfg.serifFont ? serifStackFor(cfg.serifFont) : DEFAULT_SERIF } as React.CSSProperties}
    >
      {/* HERO */}
      <section className="relative min-h-[100dvh] overflow-hidden">
        {/* Backdrop: gradient base + orientation-specific photos (portrait on
            phones, landscape on wide screens). */}
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(120% 120% at 50% 0%, color-mix(in srgb, var(--accent, #9C5B3A) 55%, #000) 0%, #050505 70%)" }}
        />
        {cfg.backdropPortrait && (
          <img src={cfg.backdropPortrait} alt="" className="absolute inset-0 h-full w-full object-cover landscape:hidden" />
        )}
        {cfg.backdrop && (
          <img
            src={cfg.backdrop}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover ${cfg.backdropPortrait ? "portrait:hidden" : ""}`}
          />
        )}
        {/* Legibility scrim, clear at the top (so the photo's branding shows),
            weighted to the bottom where the copy sits. */}
        <div
          className="absolute inset-0"
          style={{
            background: hasPhoto
              ? "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.12) 44%, rgba(0,0,0,0.72) 84%, rgba(0,0,0,0.92) 100%)"
              : "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.85) 100%)",
          }}
        />

        <div
          className={`relative flex min-h-[100dvh] flex-col items-center px-6 text-center ${hasPhoto ? "justify-end pb-14" : "justify-center"}`}
          style={cfg.heroTextColor ? { color: cfg.heroTextColor } : undefined}
        >
          <div className="w-full max-w-xl">
            {eyebrow && (
              <p className="text-[11px] font-semibold uppercase tracking-[0.42em]" style={{ color: cfg.heroTextColor ?? "rgba(255,255,255,0.7)" }}>
                {eyebrow}
              </p>
            )}

            {cfg.logo ? (
              <img src={cfg.logo} alt={name} className="mx-auto mt-6 w-56 max-w-[72vw] object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.6)]" />
            ) : !cfg.hideName ? (
              <h1
                className="mt-4 text-5xl font-black leading-[0.95] tracking-tight drop-shadow-[0_6px_30px_rgba(0,0,0,0.6)] md:text-7xl"
                style={{
                  color: cfg.heroTextColor ?? "#ffffff",
                  // Optional: use the same serif as H2s so the name doesn't
                  // clash with section headers (Tyler @ Toka 2026-08-25).
                  ...(cfg.nameUsesSerif ? { fontFamily: SERIF } : {}),
                }}
              >
                {name}
              </h1>
            ) : null}

            <p
              className={`mx-auto max-w-md text-balance text-2xl font-bold leading-tight tracking-tight md:text-3xl ${cfg.hideName && !cfg.logo ? "mt-4" : "mt-6"}`}
              style={{ fontFamily: SERIF }}
            >
              {headline}
            </p>
            <p className="mx-auto mt-3 max-w-md text-sm md:text-base" style={{ color: cfg.heroTextColor ? undefined : "rgba(255,255,255,0.7)", opacity: cfg.heroTextColor ? 0.8 : 1 }}>
              {subline}
            </p>

            {cfg.ctaHref ? (
              // Launch flip: a booking link instead of the mailing-list form.
              // For a same-origin path (e.g. /book) we intercept the click,
              // trigger the fade-out class on the splash root, then
              // client-side navigate 220ms later so the transition feels
              // smooth rather than a hard reload. External hrefs behave as
              // a plain anchor. Motion-safe: prefers-reduced-motion skips
              // the animation via the CSS keyframe guard.
              <a
                href={cfg.ctaHref}
                onClick={(e) => {
                  const href = cfg.ctaHref!;
                  if (!isSameOriginPath(href)) return; // let the browser handle external links
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // preserve "open in new tab" affordances
                  e.preventDefault();
                  setLeaving(true);
                  // Two things must be true before we navigate: (1) the 220ms
                  // fade-out has run, (2) the /book route bundle is loaded.
                  // Preload usually wins the race (kicked off on mount) but a
                  // fast click on a slow network can beat it, and the router's
                  // default lazy fallback is null (renders white). Wait on the
                  // bundle explicitly so the swap is always fade -> book, never
                  // fade -> white -> book.
                  const nextBundle = href === "/book" ? import("./book-entry") : Promise.resolve();
                  const minFade = new Promise((r) => window.setTimeout(r, 220));
                  Promise.all([minFade, nextBundle]).then(() => {
                    navigate({ to: href });
                  });
                }}
                className="mx-auto mt-9 inline-flex h-12 items-center justify-center rounded-full px-10 text-[15px] font-bold tracking-tight transition hover:brightness-110 active:scale-[0.98]"
                style={{ backgroundColor: "var(--accent, #9C5B3A)", color: "var(--accent-fg, #fff)" }}
              >
                {cfg.ctaLabel ?? "Book"}
              </a>
            ) : status === "done" ? (
              <div className="mx-auto mt-9 max-w-sm rounded-full border border-white/15 bg-white/5 px-6 py-4 text-sm">
                {successMessage}
              </div>
            ) : (
              // Stack on phones (button below the field, both full-width +
              // centred) so a long CTA label can never run off-screen; inline
              // on wider screens.
              <form
                onSubmit={submit}
                className={`mx-auto mt-9 flex max-w-sm flex-col items-stretch gap-2 ${collectPhone || collectName ? "" : "sm:flex-row sm:items-center"}`}
              >
                {collectName && (
                  <input
                    type="text"
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Name"
                    aria-label="Name"
                    className="h-12 w-full rounded-full border border-white/20 bg-white/5 px-5 text-[15px] text-white placeholder-white/40 outline-none transition focus:border-white/50 focus:bg-white/10"
                  />
                )}
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="h-12 w-full rounded-full border border-white/20 bg-white/5 px-5 text-[15px] text-white placeholder-white/40 outline-none transition focus:border-white/50 focus:bg-white/10 sm:flex-1"
                />
                {collectPhone && (
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={phoneRequired ? "Mobile number" : "Mobile number (optional)"}
                    aria-label="Mobile number"
                    className="h-12 w-full rounded-full border border-white/20 bg-white/5 px-5 text-[15px] text-white placeholder-white/40 outline-none transition focus:border-white/50 focus:bg-white/10"
                  />
                )}
                <button
                  type="submit"
                  disabled={!emailValid || !phoneOk || status === "loading"}
                  className="h-12 w-full shrink-0 rounded-full px-6 text-[15px] font-bold tracking-tight transition hover:brightness-110 active:scale-[0.98] disabled:opacity-40 sm:w-auto"
                  style={{ backgroundColor: "var(--accent, #9C5B3A)", color: "var(--accent-fg, #fff)" }}
                >
                  {status === "loading" ? "…" : cta}
                </button>
              </form>
            )}
            {error && <p className="mt-3 text-sm text-red-300">{error}</p>}

            {socialLinks.length > 0 && (
              <div className="mt-8 flex items-center justify-center gap-3">
                {socialLinks.map(([k, href]) => (
                  <a
                    key={k}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={k}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 transition hover:bg-white/10"
                    style={cfg.heroTextColor ? { color: cfg.heroTextColor, borderColor: cfg.heroTextColor } : undefined}
                  >
                    {SOCIAL_PATHS[k] ? (
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
                        <path d={SOCIAL_PATHS[k]} />
                      </svg>
                    ) : (
                      <span className="text-xs font-semibold">↗</span>
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {cfg.ritual && <RitualSection r={cfg.ritual} />}
      {cfg.land && <LandSection l={cfg.land} />}

      {/* Foot-of-page launch waitlist, a richer, designed capture (name/email/
          phone + optional "which window?" radios). Mailing-list mode only, and
          only when there are sections to scroll past to reach it. */}
      {hasSections && !cfg.ctaHref && venue?.id && <LaunchWaitlist venueId={venue.id} cfg={cfg} />}

      {/* Bottom footer bar, the natural place a user expects socials + copyright.
          The hero also renders socials, but on smaller phones the hero stack
          can push them below the fold; this bar guarantees Tyler's saved
          Instagram / Facebook icons are always reachable by scrolling to the
          bottom (Toka feedback 2026-08-24 + 2026-08-26). */}
      <ComingSoonFooter
        venueName={venue?.appName || venue?.name || ""}
        socials={socialLinks}
      />
    </div>
  );
}

function ComingSoonFooter({
  venueName,
  socials,
}: {
  venueName: string;
  socials: [string, string][];
}) {
  const hasSocials = socials.length > 0;
  const brand = venueName.trim();
  return (
    <footer className="border-t border-white/10 bg-black px-6 py-8 text-center">
      {hasSocials && (
        <div className="mb-4 flex items-center justify-center gap-3">
          {socials.map(([k, href]) => (
            <a
              key={k}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={k}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white transition hover:bg-white/10"
            >
              {SOCIAL_PATHS[k] ? (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
                  <path d={SOCIAL_PATHS[k]} />
                </svg>
              ) : (
                <span className="text-xs font-semibold">↗</span>
              )}
            </a>
          ))}
        </div>
      )}
      <p className="text-[11px] text-white/50">
        © {new Date().getFullYear()}
        {brand ? ` ${brand}` : ""}. All rights reserved.
      </p>
    </footer>
  );
}

// Foot-of-page "launch waitlist", the two-column designed capture (copy left;
// Name / Email / Phone + optional window radios right) on the accent background.
// Richer than the hero's single email field. Self-contained state so it's
// independent of the hero form.
function LaunchWaitlist({ venueId, cfg }: { venueId: string; cfg: ComingSoonConfig }) {
  const w = cfg.waitlist ?? {};
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [win, setWin] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");
  const [error, setError] = useState("");
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const phoneRequired = !!cfg.phoneRequired;
  const phoneOk = !phoneRequired || phone.replace(/\D/g, "").length >= 6;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!emailValid || !phoneOk || status === "loading") return;
    pushCtaClick("subscribe");
    setStatus("loading");
    setError("");
    try {
      // Window preference (Toka's "which window") is captured via `source` so it
      // needs no dedicated column, e.g. "coming_soon:oct-dec-2026".
      await joinMailingList(venueId, email.trim(), win ? `coming_soon:${win}` : "coming_soon", phone || undefined, undefined, name || undefined);
      pushFormInteraction("success");
      setStatus("done");
    } catch (err) {
      pushFormInteraction("fail");
      setError(err instanceof Error ? err.message : "Something went wrong, try again.");
      setStatus("idle");
    }
  }

  // Section colours: default to the venue accent bg + dark ink; editable.
  const bg = w.bgColor || EDITORIAL_ACCENT;
  const text = w.textColor || "#241a15";
  const successMessage = cfg.successMessage ?? "You're on the list. We'll be in touch the moment doors open.";
  const field =
    "w-full rounded-none border border-black/15 bg-[#efe9df] px-4 py-3.5 text-[15px] text-[#241a15] placeholder-black/45 outline-none transition focus:border-black/45";

  return (
    <section className="px-6 py-20 md:py-28" style={{ backgroundColor: bg, color: text }}>
      <div className="mx-auto grid max-w-5xl gap-10 md:grid-cols-2 md:items-start md:gap-16">
        {/* Left, copy */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.42em]">{w.eyebrow ?? "Launch waitlist"}</p>
          <h2 className="mt-5 text-5xl leading-[1.02] md:text-6xl" style={{ fontFamily: SERIF }}>
            {w.headline ?? "Get on the list."}
          </h2>
          {(w.body ?? cfg.subline) && (
            <p className="mt-6 max-w-md text-[15px] leading-relaxed" style={{ opacity: 0.75 }}>{w.body ?? cfg.subline}</p>
          )}
        </div>

        {/* Right, form */}
        <div>
          {status === "done" ? (
            <div className="border border-black/15 bg-[#efe9df] px-6 py-10 text-center text-[15px] text-[#241a15]">
              {successMessage}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Name" className={field} />
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={field}
              />
              <input type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={phoneRequired ? "Mobile number" : "Mobile number (optional)"} className={field} />

              {w.windows && w.windows.length > 0 && (
                <fieldset className="pt-2">
                  <legend className="text-[11px] font-semibold uppercase tracking-[0.22em]">Which window interests you?</legend>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                    {w.windows.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="waitlist-window"
                          value={opt.value}
                          checked={win === opt.value}
                          onChange={() => setWin(opt.value)}
                          className="accent-black"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              <button
                type="submit"
                disabled={!emailValid || !phoneOk || status === "loading"}
                className="mt-2 w-full bg-black py-4 text-[13px] font-bold uppercase tracking-[0.22em] text-white transition hover:brightness-125 disabled:opacity-50"
              >
                {status === "loading" ? "…" : cfg.ctaLabel || CTA_DEFAULT}
              </button>
              {error && <p className="text-sm text-red-900">{error}</p>}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function RitualSection({ r }: { r: NonNullable<ComingSoonConfig["ritual"]> }) {
  const steps = r.steps ?? [];
  return (
    <section className="bg-[#191817] px-6 py-20 md:py-28">
      <div className="mx-auto max-w-4xl">
        {r.eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.42em]" style={{ color: EDITORIAL_ACCENT }}>
            {r.eyebrow}
          </p>
        )}
        {r.headline && (
          <h2 className="mt-5 max-w-3xl text-balance text-4xl leading-[1.05] text-white md:text-5xl" style={{ fontFamily: SERIF }}>
            {r.headline}
          </h2>
        )}
        {/* Each step reads ACROSS, mark on the left, copy on the right, on
            every screen size (Tyler's ask), with even gaps (no asymmetric
            divider padding). */}
        <div className="mt-14 grid gap-10 md:grid-cols-3 md:gap-8">
          {steps.map((s, i) => {
            const color = s.color ?? (i === 0 ? EDITORIAL_ACCENT : "rgba(255,255,255,0.32)");
            return (
              <div key={i} className="flex items-start gap-5">
                {r.markUrl && (
                  <span
                    aria-hidden
                    className="block shrink-0"
                    style={{
                      width: 32,
                      height: 62,
                      backgroundColor: color,
                      WebkitMaskImage: `url(${r.markUrl})`,
                      maskImage: `url(${r.markUrl})`,
                      WebkitMaskSize: "contain",
                      maskSize: "contain",
                      WebkitMaskRepeat: "no-repeat",
                      maskRepeat: "no-repeat",
                      WebkitMaskPosition: "left center",
                      maskPosition: "left center",
                    }}
                  />
                )}
                <div className="min-w-0">
                  <div className="text-3xl font-bold leading-none" style={{ fontFamily: SERIF, color }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-3 text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{s.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LandSection({ l }: { l: NonNullable<ComingSoonConfig["land"]> }) {
  return (
    <section className="grid bg-[#efe9df] text-[#26231f] md:grid-cols-2">
      <div className="relative min-h-[260px] md:min-h-[560px]">
        {l.photo ? (
          <img src={l.photo} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full bg-[#dcd6c8]" />
        )}
      </div>
      <div className="flex items-center px-6 py-16 md:px-14 md:py-24">
        <div className="max-w-md">
          {l.eyebrow && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.42em]" style={{ color: EDITORIAL_ACCENT }}>
              {l.eyebrow}
            </p>
          )}
          {l.headline && (
            <h2 className="mt-4 text-balance text-4xl leading-[1.08] md:text-5xl" style={{ fontFamily: SERIF }}>
              {l.headline}
            </h2>
          )}
          <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-[#4a453e]">
            {(l.body ?? []).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          {l.ctaLabel && (
            <a
              href={l.ctaHref ?? "#"}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-block border-b pb-1 text-[11px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: EDITORIAL_ACCENT, borderColor: EDITORIAL_ACCENT }}
            >
              {l.ctaLabel} →
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
